/**
 * Service for fetching and searching Bouwbesluit articles.
 */

import { ref, getDownloadURL } from "firebase/storage";

import { getFirebaseStorage } from "@/lib/firebase";
import type { Article } from "@/features/gebreken/types/article";

/**
 * Path to the artikelen.json file in Firebase Storage.
 */
const ARTICLES_PATH = "BWBR0041297/artikelen.json";

/**
 * Convert Firebase Storage URL to use local proxy in development.
 * This avoids CORS issues when fetching from Firebase Storage.
 */
function proxyUrl(url: string): string {
  if (import.meta.env.DEV) {
    return url.replace(
      "https://firebasestorage.googleapis.com",
      "/firebase-storage"
    );
  }
  return url;
}

/**
 * Fetch all articles from Firebase Storage.
 * Uses Firebase SDK to get a signed URL with the user's auth token.
 * In development, the URL is proxied through Vite to avoid CORS issues.
 * @returns Promise resolving to array of all articles
 */
export async function fetchAllArticles(): Promise<Article[]> {
  const storage = getFirebaseStorage();
  const fileRef = ref(storage, ARTICLES_PATH);
  const url = await getDownloadURL(fileRef);

  // Use globalThis.fetch for Firebase Storage (not backend API)
  const response = await globalThis.fetch(proxyUrl(url));

  if (!response.ok) {
    throw new Error(`Failed to fetch articles: ${response.status} ${response.statusText}`);
  }

  const articles: Article[] = await response.json();
  return articles;
}

/**
 * Filter articles by their article numbers.
 * Used to get full article data after API returns matching numbers.
 *
 * @param articles - Full list of articles
 * @param numbers - Array of article numbers to filter by
 * @returns Filtered articles matching the given numbers, in the order of numbers
 */
export function filterArticlesByNumbers(
  articles: Article[],
  numbers: string[]
): Article[] {
  // Create a map for O(1) lookup
  const articleMap = new Map(articles.map((article) => [article.nummer, article]));

  // Return articles in the order of the numbers array (preserves API ranking)
  const filtered: Article[] = [];
  for (const nummer of numbers) {
    const article = articleMap.get(nummer);
    if (article) {
      filtered.push(article);
    }
  }

  return filtered;
}

/**
 * Simple text filter for articles.
 * Searches in nummer, titel, and tekst fields (case-insensitive).
 *
 * @param articles - All articles to filter
 * @param query - Search query
 * @returns Filtered articles matching the query
 */
export function filterArticles(articles: Article[], query: string): Article[] {
  const searchTerm = query.toLowerCase().trim();
  if (!searchTerm) {
    return articles;
  }

  return articles.filter((article) => {
    // Search in nummer
    if (article.nummer.toLowerCase().includes(searchTerm)) {
      return true;
    }
    // Search in titel
    if (article.titel.toLowerCase().includes(searchTerm)) {
      return true;
    }
    // Search in tekst
    if (article.tekst?.toLowerCase().includes(searchTerm)) {
      return true;
    }
    return false;
  });
}

/**
 * Sort articles by their article number.
 * Handles both simple numbers (1.1, 2.3) and complex ones (1.1a, 2.3b).
 *
 * @param articles - Articles to sort
 * @returns Sorted articles array
 */
export function sortArticlesByNumber(articles: Article[]): Article[] {
  return [...articles].sort((a, b) => {
    // Split into main number and suffix (e.g., "1.1a" -> ["1.1", "a"])
    const parseNumber = (nummer: string) => {
      const match = nummer.match(/^(\d+(?:\.\d+)?)([a-z]?)$/);
      if (!match) return { main: nummer, suffix: "" };
      return { main: match[1], suffix: match[2] || "" };
    };

    const aParsed = parseNumber(a.nummer);
    const bParsed = parseNumber(b.nummer);

    // Compare main numbers first
    const aMain = aParsed.main.split(".").map(Number);
    const bMain = bParsed.main.split(".").map(Number);

    for (let i = 0; i < Math.max(aMain.length, bMain.length); i++) {
      const aNum = aMain[i] ?? 0;
      const bNum = bMain[i] ?? 0;
      if (aNum !== bNum) return aNum - bNum;
    }

    // If main numbers are equal, compare suffixes
    return aParsed.suffix.localeCompare(bParsed.suffix);
  });
}
